'use client';

import { Plus, MessageSquare, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ChatSession } from '@/types/chat';

interface ChatSidebarProps {
  sessions: ChatSession[];
  selectedSessionId: string | null;
  onSelectSession: (sessionId: string) => void;
  onNewSession: () => void;
  onDeleteSession: (sessionId: string) => void;
  isCreating?: boolean;
  isDeleting?: string | null;
}

/**
 * Sidebar showing chat session history.
 *
 * Features:
 * - New chat button
 * - List of past sessions with titles
 * - Delete session button (on hover)
 * - Active session highlighting
 */
export function ChatSidebar({
  sessions,
  selectedSessionId,
  onSelectSession,
  onNewSession,
  onDeleteSession,
  isCreating = false,
  isDeleting = null,
}: ChatSidebarProps) {
  return (
    <div className="w-64 border-r border-border-subtle bg-surface-raised flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-border-subtle">
        <Button onClick={onNewSession} disabled={isCreating} variant="coral" className="w-full justify-center gap-2">
          {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          New Chat
        </Button>
      </div>

      {/* Session List */}
      <div className="flex-1 overflow-y-auto p-2">
        {sessions.length === 0 ? (
          <div className="text-center text-text-placeholder text-sm py-8 px-4">
            No conversations yet. Start a new chat!
          </div>
        ) : (
          <div className="space-y-1">
            {sessions.map(session => (
              <SessionItem
                key={session.id}
                session={session}
                isSelected={session.id === selectedSessionId}
                isDeleting={isDeleting === session.id}
                onSelect={() => onSelectSession(session.id)}
                onDelete={() => onDeleteSession(session.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface SessionItemProps {
  session: ChatSession;
  isSelected: boolean;
  isDeleting: boolean;
  onSelect: () => void;
  onDelete: () => void;
}

function SessionItem({ session, isSelected, isDeleting, onSelect, onDelete }: SessionItemProps) {
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete();
  };

  return (
    <div
      onClick={onSelect}
      className={cn(
        'group flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-all',
        isSelected ? 'bg-coral/10 text-coral border-l-2 border-coral' : 'hover:bg-surface-overlay text-text-primary'
      )}
    >
      {/* Icon */}
      <MessageSquare className={cn('w-4 h-4 flex-shrink-0', isSelected ? 'text-coral' : 'text-text-placeholder')} />

      {/* Title */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{session.title || 'New conversation'}</p>
        {session.company_name && <p className="text-xs text-blue-500 truncate">{session.company_name}</p>}
        <p className="text-xs text-text-placeholder truncate">{formatDate(session.updated_at)}</p>
      </div>

      {/* Delete Button (on hover) */}
      <button
        onClick={handleDelete}
        disabled={isDeleting}
        className={cn(
          'p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity',
          'hover:bg-surface-overlay text-text-placeholder hover:text-red-500',
          isDeleting && 'opacity-100'
        )}
      >
        {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
      </button>
    </div>
  );
}

/**
 * Format ISO date to relative or short date string.
 */
function formatDate(isoString: string): string {
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  } catch {
    return '';
  }
}
